import { create } from 'zustand';
import type { RoomDb } from '../api/rooms.js';

/**
 * HM-073 — Walk-in wizard state (4 step).
 */
export type WalkInMode = 'day_use_4h' | 'day_use_8h' | 'overnight';
/**
 * Revisione pagamenti 2026-07-22: terza opzione 'later' = "Paga all'uscita".
 * Con 'later' la cascade NON chiama /payments ne' /fiscal-receipts (scontrino
 * unificato al checkout) e la prenotazione resta paid_amount=0 → badge
 * "DA INCASSARE" su dashboard/camere finche' il checkout non incassa.
 */
export type WalkInPayment = 'cash' | 'card' | 'later';

export type WalkInStep = 1 | 2 | 3 | 4;

// HM-346 cluster 16.1.5: estensione campi separati firstName/lastName +
// document. fullName e' derivato (concat) per backward-compat downstream
// (StepRecap, walk-in-cascade.guestName).
export type WalkInGuest = {
  firstName: string;
  lastName: string;
  fullName: string;
  phone?: string;
  email?: string;
  documentType?: string;
  documentNumber?: string;
  /** ID guest pre-esistente quando l'utente seleziona da search. */
  existingGuestId?: string;
};

// HM-343 cluster 16.1: extension dynamic pricing.
// selectedHours: ore custom (1-24), null se overnight.
// selectedMode: 'hours' | 'overnight'.
// priceCalc: ultimo risultato preview-calc (cache UI).
// hasWeekendWarning / hasHolidayWarning: derivati da priceCalc.
export type WalkInDynamicPricing = {
  selectedHours: number;
  selectedMode: 'hours' | 'overnight';
  priceCalc: {
    total: number;
    breakdown: { label: string; amount: number }[];
    appliedCap: 'none' | 'overnight' | 'weekend' | 'holiday';
  } | null;
  hasWeekendWarning: boolean;
  hasHolidayWarning: boolean;
};

export type WalkInState = {
  isOpen: boolean;
  step: WalkInStep;
  room: RoomDb | null;
  mode: WalkInMode;
  guest: WalkInGuest;
  payment: WalkInPayment;
  /**
   * HM-412 cluster 17.2 polish: ora di check-in preselezionata.
   * Setata da Planning Gantt onSlotClick (giorno+ora reale cliccata,
   * Week view → giorno selezionato con ora default 14:00). null = wizard
   * usa `new Date()` come default come da pre-cluster17.2.
   */
  preselectedStart: Date | null;
  /** Dynamic pricing scelta utente (HM-343 cluster 16.1) */
  dynamicPricing: WalkInDynamicPricing;
  /** HM-350 cluster 16.1.5 anonymous flow Day Use (skip Step 2). */
  isAnonymous: boolean;
  anonymousId: string | null;
  /** Esecuzione cascade in progress (HM-074).
   *
   * HM-355 cluster 16.1.7 graceful degradation: step 1 (booking) hard
   * required. Step 2-7 (payment, fiscal, access_code, messaging,
   * confirm, check_in) soft: failure popola warnings[] ma cascade
   * prosegue, booking salvato + camera passa OCCUPATA via check-in
   * side-effect rooms.status='occupied'.
   */
  cascade: {
    running: boolean;
    currentStep:
      | 'idle'
      | 'booking'
      | 'payment'
      | 'fiscal'
      | 'access_code'
      | 'messaging'
      | 'confirm'
      | 'check_in'
      | 'done'
      | 'done_with_warnings'
      | 'error';
    bookingId: string | null;
    accessCode: string | null;
    /** Hard error: blocca cascade (solo step 1 booking lo popola). */
    error: { stage: string; message: string } | null;
    /** Soft warnings raccolti durante step 2-7 graceful (HM-355). */
    warnings: { stage: string; message: string }[];
  };
  /**
   * Apre il wizard walk-in per `room`. Se `preselectedStart` viene passato
   * (HM-412 cluster 17.2 polish), il wizard parte con quell'ora di
   * check-in invece che con `new Date()`.
   */
  open: (room: RoomDb, preselectedStart?: Date | null) => void;
  close: () => void;
  setStep: (s: WalkInStep) => void;
  setMode: (m: WalkInMode) => void;
  setGuest: (g: Partial<WalkInGuest>) => void;
  setPayment: (p: WalkInPayment) => void;
  setCascade: (c: Partial<WalkInState['cascade']>) => void;
  setDynamicPricing: (p: Partial<WalkInDynamicPricing>) => void;
  setAnonymous: (flag: boolean) => void;
  reset: () => void;
};

/**
 * Genera un ID anonymous "Walk-in #YYYY-MM-DD-NNN" per booking
 * walk-in senza dati cliente. NNN = secondi dall'inizio del giorno
 * mod 1000 per ordinamento stabile.
 */
function generateAnonymousId(now: Date = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  const secOfDay = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const nnn = String(secOfDay % 1000).padStart(3, '0');
  return `Walk-in #${y}-${m}-${d}-${nnn}`;
}

const initialCascade: WalkInState['cascade'] = {
  running: false,
  currentStep: 'idle',
  bookingId: null,
  accessCode: null,
  error: null,
  warnings: [],
};

const initialDynamicPricing: WalkInDynamicPricing = {
  selectedHours: 2,
  selectedMode: 'hours',
  priceCalc: null,
  hasWeekendWarning: false,
  hasHolidayWarning: false,
};

export const useWalkInWizard = create<WalkInState>((set) => ({
  isOpen: false,
  step: 1,
  room: null,
  mode: 'day_use_4h',
  guest: { firstName: '', lastName: '', fullName: '' },
  payment: 'card',
  cascade: initialCascade,
  dynamicPricing: initialDynamicPricing,
  preselectedStart: null,
  isAnonymous: false,
  anonymousId: null,
  open: (room, preselectedStart = null) =>
    set({
      isOpen: true,
      step: 1,
      room,
      mode: 'day_use_4h',
      guest: { firstName: '', lastName: '', fullName: '' },
      payment: 'card',
      cascade: initialCascade,
      dynamicPricing: initialDynamicPricing,
      preselectedStart,
      isAnonymous: false,
      anonymousId: null,
    }),
  close: () => set({ isOpen: false }),
  setStep: (s) => set({ step: s }),
  setMode: (m) => set({ mode: m }),
  setGuest: (g) =>
    set((cur) => {
      const merged = { ...cur.guest, ...g };
      // Sync fullName se cambia firstName/lastName e fullName non e' stato
      // settato esplicitamente nello stesso patch.
      if (('firstName' in g || 'lastName' in g) && !('fullName' in g)) {
        merged.fullName = `${merged.firstName ?? ''} ${merged.lastName ?? ''}`.trim();
      }
      return { guest: merged };
    }),
  setPayment: (p) => set({ payment: p }),
  setCascade: (c) => set((cur) => ({ cascade: { ...cur.cascade, ...c } })),
  setDynamicPricing: (p) => set((cur) => ({ dynamicPricing: { ...cur.dynamicPricing, ...p } })),
  setAnonymous: (flag) =>
    set((cur) => {
      if (!flag) {
        return { isAnonymous: false, anonymousId: null };
      }
      // Anonymous: genera ID + svuota nome cliente per evitare leak.
      return {
        isAnonymous: true,
        anonymousId: cur.anonymousId ?? generateAnonymousId(),
        guest: { firstName: '', lastName: '', fullName: '' },
      };
    }),
  reset: () =>
    set({
      isOpen: false,
      step: 1,
      room: null,
      mode: 'day_use_4h',
      guest: { firstName: '', lastName: '', fullName: '' },
      payment: 'card',
      cascade: initialCascade,
      dynamicPricing: initialDynamicPricing,
      preselectedStart: null,
      isAnonymous: false,
      anonymousId: null,
    }),
}));

export function modeDurationMs(mode: WalkInMode): number {
  switch (mode) {
    case 'day_use_4h':
      return 4 * 60 * 60 * 1000;
    case 'day_use_8h':
      return 8 * 60 * 60 * 1000;
    case 'overnight':
      // 14h: from 18:00 → 08:00 typical; here we just compute end as +14h
      return 14 * 60 * 60 * 1000;
  }
}

/**
 * QA sweep 2026-07-22: durata REALE del soggiorno scelto nel wizard.
 * selectedMode='hours' → selectedHours custom (HM-353 cluster 16.1.6),
 * altrimenti fallback alla durata legacy del mode. Helper CONDIVISO tra
 * walk-in-cascade (booking creato) e StepRecap (orario mostrato): recap e
 * booking non possono raccontare due orari di uscita diversi.
 */
export function walkInDurationMs(
  mode: WalkInMode,
  dynamicPricing?: Pick<WalkInDynamicPricing, 'selectedMode' | 'selectedHours'> | null,
): number {
  if (dynamicPricing && dynamicPricing.selectedMode === 'hours') {
    return dynamicPricing.selectedHours * 60 * 60 * 1000;
  }
  return modeDurationMs(mode);
}

/**
 * Regola operativa (Michele, 2026-07-23): il pernottamento da' diritto
 * all'uscita FINO ALLE 12:00. Non e' "N ore dall'ingresso": chi entra alle
 * 20:00 o alle 02:30 esce comunque entro mezzogiorno. Dopo le 12:00 (+
 * cuscinetto listino) scattano le ore extra come da worker.
 */
export const OVERNIGHT_CHECKOUT_HOUR = 12;

/**
 * Fine soggiorno REALE del walk-in:
 * - pernottamento → il prossimo mezzogiorno (ore locali del tablet, che in
 *   struttura e' l'ora italiana): ingresso >=12:00 → domani alle 12:00;
 *   ingresso notturno (<12:00) → OGGI alle 12:00.
 * - a ore → start + durata scelta (walkInDurationMs).
 * Helper CONDIVISO cascade/recap: booking e schermata non possono
 * raccontare due orari di uscita diversi.
 */
export function walkInEndAt(
  start: Date,
  mode: WalkInMode,
  dynamicPricing?: Pick<WalkInDynamicPricing, 'selectedMode' | 'selectedHours'> | null,
): Date {
  const isOvernight =
    dynamicPricing?.selectedMode === 'overnight' ||
    (dynamicPricing?.selectedMode !== 'hours' && mode === 'overnight');
  if (isOvernight) {
    const end = new Date(start);
    end.setHours(OVERNIGHT_CHECKOUT_HOUR, 0, 0, 0);
    if (start.getHours() >= OVERNIGHT_CHECKOUT_HOUR) {
      end.setDate(end.getDate() + 1);
    }
    return end;
  }
  return new Date(start.getTime() + walkInDurationMs(mode, dynamicPricing));
}

export function modeService(mode: WalkInMode): 'day_use' | 'overnight' {
  return mode === 'overnight' ? 'overnight' : 'day_use';
}

export function modeBasePrice(mode: WalkInMode): number {
  switch (mode) {
    case 'day_use_4h':
      return 75;
    case 'day_use_8h':
      return 130;
    case 'overnight':
      return 180;
  }
}
